/**
 * Editor для ref.trade_equipment (Фаза 2.1 #1).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  refsTradeEquipment,
  type EquipmentType,
} from '@/api/refsTradeEquipment';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNextRefCode } from '@/hooks/useNextRefCode';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

const PERMISSION = 'refs.trade_equipment.manage';

interface FormState {
  organization_id: string;
  code: string;
  name: string;
  equipment_type: EquipmentType;
  serial_number: string;
  pos_workstation_id: string;
  is_active: boolean;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  code: '',
  name: '',
  equipment_type: 'scale',
  serial_number: '',
  pos_workstation_id: '',
  is_active: true,
  comment: '',
};

export default function TradeEquipmentEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['refs-trade-equipment', id],
    queryFn: () => refsTradeEquipment.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  // Авто-пропозиція коду TE-NN
  const nextCode = useNextRefCode({
    queryKey: ['refs-trade-equipment', form.organization_id, 'for-next-code'],
    queryFn: () =>
      refsTradeEquipment.list({ organization_id: form.organization_id, limit: 500 }),
    extractCodes: (data) => data.items.map((i) => i.code ?? '').filter(Boolean),
    prefix: 'TE',
    enabled: isNew && !!form.organization_id,
  });
  useEffect(() => {
    if (isNew && nextCode.suggested && !form.code) {
      setForm((f) => ({ ...f, code: nextCode.suggested }));
    }
  }, [isNew, nextCode.suggested, form.code]);

  // Workstations для прив'язки (опціональна) — у поточній організації.
  const workstations = useQuery({
    queryKey: ['retail-pos-workstations', form.organization_id, 'for-trade-equipment'],
    queryFn: () =>
      retailPosWorkstations.list({
        organization_id: form.organization_id,
        limit: 200,
      }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        code: d.code ?? '',
        name: d.name,
        equipment_type: d.equipment_type,
        serial_number: d.serial_number ?? '',
        pos_workstation_id: d.pos_workstation_id ?? '',
        is_active: d.is_active,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const payload = () => ({
    code: form.code || null,
    name: form.name,
    equipment_type: form.equipment_type,
    serial_number: form.serial_number || null,
    pos_workstation_id: form.pos_workstation_id || null,
    is_active: form.is_active,
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () =>
      refsTradeEquipment.create({
        organization_id: form.organization_id,
        ...payload(),
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['refs-trade-equipment'] });
      navigate(`/retail/trade-equipment/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => refsTradeEquipment.update(id!, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['refs-trade-equipment'] });
      qc.invalidateQueries({ queryKey: ['refs-trade-equipment', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => refsTradeEquipment.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['refs-trade-equipment'] });
      navigate('/retail/trade-equipment');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        name: form.name,
        equipment_type: form.equipment_type,
      },
      {
        organization_id: 'Юрособа',
        name: 'Назва',
        equipment_type: 'Тип обладнання',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? 'Нове торгівельне обладнання'
            : `Обладнання: ${form.code || form.name}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/trade-equipment')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити обладнання?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Код</label>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            maxLength={50}
            placeholder={nextCode.suggested ?? 'Авто-пропозиція…'}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Назва</RequiredLabel>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={200}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Тип обладнання</RequiredLabel>
          <select
            value={form.equipment_type}
            onChange={(e) =>
              setForm({ ...form, equipment_type: e.target.value as EquipmentType })
            }
          >
            <option value="scale">Ваги</option>
            <option value="scanner">Сканер штрих-кодів</option>
            <option value="printer">Принтер чеків</option>
            <option value="cash_drawer">Грошова скринька</option>
            <option value="other">Інше</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Серійний номер</label>
          <input
            value={form.serial_number}
            onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
            maxLength={100}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>POS-станція (опц.)</label>
          <select
            value={form.pos_workstation_id}
            onChange={(e) => setForm({ ...form, pos_workstation_id: e.target.value })}
            disabled={!form.organization_id}
          >
            <option value="">— без прив'язки —</option>
            {workstations.data?.items.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.code}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активне
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
